import styled, { css } from "styled-components";
import { pxToRem, withAlpha } from "../../styles/utils";

interface DropZoneProps {
  $dragOver: boolean;
  $variant?: "default" | "compact";
  $disabled?: boolean;
}

export const DropZone = styled.div<DropZoneProps>`
  position: relative;
  display: flex;
  width: 100%;
  height: ${pxToRem(150)};
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xl)};
  border: ${pxToRem(1)} dashed ${({ theme }) => theme.colors.brand};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => withAlpha(theme.colors.brand, 0.17)};
  opacity: 0.6;
  text-align: center;
  cursor: pointer;

  input {
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  p {
    color: ${({ theme }) => theme.colors.grayIcon};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
    font-weight: 500;
  }

  ${({ $dragOver, theme }) =>
    $dragOver &&
    css`
      border-color: ${theme.colors.brand};
      opacity: 1;
    `}

  ${({ $variant, theme }) =>
    $variant === "compact" &&
    css`
      min-height: ${pxToRem(82)};
      height: auto;
      margin-bottom: 0;
      padding: ${pxToRem(theme.spacing.md)};
      border-color: ${withAlpha(theme.colors.ink, 0.26)};
      border-radius: ${pxToRem(theme.radii.lg)};
      background: ${theme.colors.white};
      opacity: 1;
      transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;

      &:hover {
        border-color: ${theme.colors.brand};
        background: ${theme.colors.surfaceFaint};
      }

      input:focus-visible + * {
        outline: ${pxToRem(2)} solid ${theme.colors.brand};
        outline-offset: ${pxToRem(theme.spacing.xxs)};
        border-radius: ${pxToRem(theme.radii.sm)};
      }
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: default;
      opacity: 0.55;
      pointer-events: none;
    `}
`;

export const DropZoneContent = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const DropZoneTitle = styled.strong`
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
`;

export const DropZoneCaption = styled.span`
  color: ${({ theme }) => theme.colors.inkSoft};
`;

export const InfoText = styled.div`
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xs)};
  color: ${({ theme }) => theme.colors.grayMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  text-align: center;
`;

export const FileList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xs)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xl)};
`;

export const FileItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  max-width: ${pxToRem(150)};
  padding: ${({ theme }) => pxToRem(theme.spacing.xxs)} ${({ theme }) => pxToRem(theme.spacing.xs)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.colors.grayLine};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.surfaceCool};
`;

export const DownloadableArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  padding: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.sm)};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.black, 0.05)};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const FileIcon = styled.svg`
  width: ${pxToRem(20)};
  height: ${pxToRem(20)};
  flex: 0 0 auto;
`;

export const SpinnerIconContainer = styled.div`
  position: relative;
  width: ${pxToRem(20)};
  height: ${pxToRem(20)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SpinnerIcon = styled.div<{ $frames: string[] }>`
  width: ${pxToRem(20)};
  height: ${pxToRem(20)};
  background-image: ${({ $frames }) => $frames.map((frame) => `url(${frame})`).join(", ")};
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  animation: spinner-animation 0.8s steps(4, end) infinite;

  @keyframes spinner-animation {
    0% { background-image: url(${({ $frames }) => $frames[0]}); }
    25% { background-image: url(${({ $frames }) => $frames[1]}); }
    50% { background-image: url(${({ $frames }) => $frames[2]}); }
    75% { background-image: url(${({ $frames }) => $frames[3]}); }
    100% { background-image: url(${({ $frames }) => $frames[0]}); }
  }
`;

export const ProgressPercent = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${pxToRem(8)};
  font-weight: 600;
  text-shadow: 0 0 ${pxToRem(2)} ${({ theme }) => theme.colors.shadowHeavy};
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

export const FileName = styled.span`
  width: ${pxToRem(50)};
  overflow: hidden;
  color: ${({ theme }) => theme.colors.grayText};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RemoveFileButton = styled.button`
  position: absolute;
  top: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  right: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.dangerBrightAlt};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;
